import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import { t } from "@lingui/core/macro";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";

import { Button } from "@/components/design-system/Button";
import { DropZone } from "@/overlay/components/CommonParts/DropZone";
import { PoiLayerCsvUpload } from "../LayerStackWindow/PoiLayerCsvUpload";
import { PoiColumnHeader } from "./PoiColumnHeader";
import { PoiInput } from "./PoiInput";
import { coordColDef, dataColDef, exportColumnsFromData, groupColumns } from "./utils";

import { useGetCustomGeoJsonList } from "@/api/hooks/customGeojson";
import { FeatureHubGuard } from "@/featureFlags";
import { CustomGeoJsonFileSelect } from "@/overlay/components/LayerStackWindow/CustomGeoJsonForm";
import { PoiLocationInput } from "@/overlay/components/PoiManagerWindow/PoiLocationInput";
import { usePoiTableHandlers } from "@/overlay/components/PoiManagerWindow/hooks";
import "@/style/aggrid.style.scss";
import { classNames } from "@/utility/jsx";
import "./gridStyle.scss";
import styles from "./style.module.scss";

function PoiAddColumnHeader(props: any) {
  const handleClick = () => {
    if (props.onClick) {
      props.onClick();
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div onClick={handleClick} style={{ cursor: "pointer", userSelect: "none" }}>
      + Add Property
    </div>
  );
}

type PoiTableProps = {
  initialData: PoiDataGrid;
  mapId?: number;
  onChange: (data: PoiDataGrid) => void;
};

export function PoiTable({ mapId, initialData, onChange }: PoiTableProps) {
  const tableRef = useRef<AgGridReact>(null);
  const { isLoading, list } = useGetCustomGeoJsonList();
  const {
    data,
    handleAddProperty,
    handleRemoveProperty,
    handleRemoveRows,
    handleColumnNameChange,
    handleCsvUploaded,
    handleCsvFileDrop,
    handleCellValueSetter,
    handleAddPoint,
    handleSelectRows,
    rowsSelected,
    handleExportCSV,
    setUserFileId,
    userFileId,
  } = usePoiTableHandlers({ initialData, onChange, tableRef });

  // Workaround for Ag-Grid + React fundamental issue
  // https://stackoverflow.com/questions/67617716/ag-grid-prevents-access-to-current-value-of-react-state-variable
  const dataRef = useRef<{ [column: string]: any }[]>([]);
  // make stateRef always have the current count
  // your "fixed" callbacks can refer to this object whenever
  // they need the current value.  Note: the callbacks will not
  // be reactive - they will not re-run the instant state changes,
  // but they *will* see the current value whenever they do run
  // columnsDefRef.current = columnsDef;
  dataRef.current = data;

  const colDefs = useMemo<ColDef[]>(() => {
    if (!data.length) {
      return [];
    }

    const checkboxColumns: ColDef = {
      checkboxSelection: true,
      lockPosition: true,
      headerCheckboxSelection: true,
      resizable: false,
      editable: false,
      sortable: false,
      pinned: true,
      maxWidth: 34,
      cellClass: "ag-center-cell",
    };

    const columns = exportColumnsFromData(data);
    const { coordCols, dataCols } = groupColumns(columns);

    const coordColumns = coordCols.map((column) => ({
      ...coordColDef(column),
      valueSetter: handleCellValueSetter,
    }));
    const dataColumns = dataCols.map((column) => ({
      ...dataColDef(column),
      valueSetter: handleCellValueSetter,
      headerComponent: PoiColumnHeader,
      headerComponentParams: {
        onRemoveColumn: handleRemoveProperty,
        onColumnNameChange: handleColumnNameChange,
      },
    }));

    const addColumn: ColDef = {
      headerName: "Add column",
      field: "add_property",
      editable: false,
      sortable: false,
      suppressMenu: true,
      suppressMovable: true,
      pinned: "right",
      type: "addColumn",
      headerComponent: PoiAddColumnHeader,
      headerComponentParams: {
        onClick: handleAddProperty,
      },
    };

    return [checkboxColumns, ...coordColumns, ...dataColumns, addColumn];
  }, [data, handleCellValueSetter, handleRemoveProperty, handleColumnNameChange, handleAddProperty]);

  return (
    <DropZone placeholder={t`Upload CSV files`} type="text/csv" onDrop={handleCsvFileDrop}>
      <div className={styles["poi-panel"]} id={"poi-panel"}>
        {mapId ? <PoiInput mapId={mapId} onApply={handleAddPoint} /> : <PoiLocationInput onApply={handleAddPoint} />}
        <div>
          <PoiLayerCsvUpload type="button" onCsvUpload={handleCsvUploaded} />
          <FeatureHubGuard flag={"eon_table"}>
            {!isLoading && list && (
              <CustomGeoJsonFileSelect list={list} selectedFileId={userFileId} onChange={setUserFileId} />
            )}
          </FeatureHubGuard>
        </div>
        <div className={classNames(styles["poi-panel-grid"])}>
          <AgGridReact
            className={"ag-theme-alpine"}
            ref={tableRef}
            rowData={data}
            columnDefs={colDefs}
            suppressHeaderFocus={true}
            onSelectionChanged={handleSelectRows}
            rowSelection="multiple"
            headerHeight={30}
            rowHeight={28}
            autoSizeStrategy={{
              type: "fitCellContents",
            }}
            suppressRowClickSelection={true}
          />
        </div>
        <div className={styles["poi-panel-bottom-bar"]}>
          <Button
            variant="outline"
            size="md"
            leftIcon={<span className="material-icons">delete</span>}
            onClick={handleRemoveRows}
            disabled={!rowsSelected}
          >
            {t`Remove Selected Points`}
          </Button>
          <Button
            size="md"
            variant="secondary"
            leftIcon={<span className="material-icons">download</span>}
            onClick={handleExportCSV}
          >
            {t`Export CSV`}
          </Button>
        </div>
        <div className={styles["poi-panel__footer"]}>
          <a href="/poi/example.csv" download="example.csv" target="_blank" rel="noreferrer">
            {t`Download example CSV`}
          </a>
        </div>
      </div>
    </DropZone>
  );
}
